import type { ProductView } from '@khaddi/contracts';
import { colors, CONTENT_MAX_WIDTH, Cup, formatVnd, Grid, Icon, SIDE_NAV_WIDTH, Text, useLayout, useRouter } from '@khaddi/ui';
import { Pressable, useWindowDimensions, View } from 'react-native';

/** Grid tile from the home design: tinted cup square, HOT badge, name, price and a + affordance. */
export const ProductCard = ({ product }: { product: ProductView }) => {
  const { navigate } = useRouter();
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={`${product.name}, ${formatVnd(product.priceM)}${product.available ? '' : ', tạm hết'}`}
      onPress={() => navigate(`/product/${encodeURIComponent(product.id)}`)}
      style={({ pressed }) => ({
        flex: 1,
        backgroundColor: colors.surface,
        borderRadius: 20,
        paddingTop: 10,
        paddingHorizontal: 10,
        paddingBottom: 12,
        gap: 6,
        boxShadow: '0 1px 2px rgba(28, 69, 95,.06)',
        opacity: product.available ? 1 : 0.6,
        transform: [{ scale: pressed ? 0.97 : 1 }],
      })}
    >
      <View style={{ height: 110, borderRadius: 14, backgroundColor: product.background, alignItems: 'center', justifyContent: 'flex-end' }}>
        {product.bestSeller ? (
          <View style={{ position: 'absolute', top: 8, left: 8, backgroundColor: colors.red, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999 }}>
            <Text variant="label" color="#FFFFFF" style={{ fontSize: 10, fontWeight: '700' }}>
              HOT
            </Text>
          </View>
        ) : null}
        {!product.available ? (
          <View style={{ position: 'absolute', top: 8, right: 8, backgroundColor: colors.navy, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999 }}>
            <Text variant="label" color="#FFFFFF" style={{ fontSize: 10, fontWeight: '700' }}>
              Tạm hết
            </Text>
          </View>
        ) : null}
        <Cup tint={product.tint} width={72} imageUrl={product.imageUrl} />
      </View>
      <Text variant="bodyStrong" numberOfLines={2} style={{ lineHeight: 17.5, minHeight: 35 }}>
        {product.name}
      </Text>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text variant="bodyStrong" color={colors.primaryText} numeric>
          {formatVnd(product.priceM)}
        </Text>
        <View style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="plus" size={16} color={colors.onPrimary} strokeWidth={3} />
        </View>
      </View>
    </Pressable>
  );
};

/** Two across on phones and tablets; three or four on desktop, depending on the room the side nav leaves. */
const useProductColumns = (): number => {
  const { size, navLayout } = useLayout();
  const { width } = useWindowDimensions();
  if (size !== 'desktop') return 2;
  const content = Math.min(width - (navLayout === 'side' ? SIDE_NAV_WIDTH.desktop : 0), CONTENT_MAX_WIDTH) - 32;
  return content >= 1100 ? 4 : 3;
};

export const ProductGrid = ({ products }: { products: ProductView[] }) => (
  <Grid columns={useProductColumns()}>
    {products.map((product) => (
      <ProductCard key={product.id} product={product} />
    ))}
  </Grid>
);
